import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { BookableAssetCreateRequest } from "@/api/types";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { parseAsNumber } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";

import { defaultAmountSeats } from "../../constants";
import { BookableAssetForm, type BookableAssetFormProps } from "./BookableAssetForm";
import { formatBookableDaysPayload, getDefaultFormValues } from "./helpers";
import type { AssetObserver, CreateOrEditAssetFormValues } from "./types";

export function CreateBookableAssetPage(): React.ReactNode {
  const slug = useSlug();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const { isUploadingDocument, uploadFormDocument } = useUploadDocument();
  const sessionUser = useSessionUser();
  const navigate = useNavigate();

  const integrationQueries = useIntegrationQueries();
  const { data: outlookConfiguration, isFetching: isFetchingOutlookConfiguration } = useQuery(
    integrationQueries.outlookConfiguration(),
  );
  const { data: jimConfiguration, isFetching: isFetchingJimConfiguration } = useQuery(
    integrationQueries.jimConfiguration(),
  );
  const { data: jimRooms = [], isFetching: isFetchingJimRooms } = useQuery({
    ...integrationQueries.jimRooms(),
    enabled: jimConfiguration?.enabled === true,
  });
  const { mutateAsync: getBookingsToBeCancelled, isPending: isPendingBookingsToBeCancelled } =
    bookableAssetMutations.useGetBookingsToBeCancelled();
  const createAssetMutation = bookableAssetMutations.useAddAsset();

  async function handleSubmit(formValues: CreateOrEditAssetFormValues) {
    const imageIds: string[] | undefined = [];
    for (const image of formValues.images) {
      const uploadedImage = await uploadFormImage(image);
      if (uploadedImage) {
        imageIds.push(uploadedImage.id);
      }
    }

    let documentId: string | undefined;
    if (formValues.regulationDocument && formValues.regulationDocument.length > 0) {
      const uploadedDocument = await uploadFormDocument(formValues.regulationDocument[0]);
      if (uploadedDocument) {
        documentId = uploadedDocument.id;
      }
    }

    const data: BookableAssetCreateRequest = {
      audience: formValues.audience,
      name: formValues.name,
      description: formValues.description,
      images: imageIds.length > 0 ? imageIds : [],
      sizeSpecification: formValues.sizeSpecification,
      pricePerHourSpecification: parseAsNumber(formValues.pricePerHourSpecification),
      capacitySpecification: parseAsNumber(formValues.capacitySpecification),
      locationSpecification: formValues.locationSpecification,
      regulationDocumentId: formValues.regulationsApplicable ? documentId : undefined,
      bookableDays: formatBookableDaysPayload(formValues.bookableDays),
      timeslot: formValues.timeslot || "allDay",
      customTimeslot: formValues.customTimeslot,
      canBookMultipleSlots: formValues.canBookMultipleSlots,
      notificationEnabled: formValues.notificationEnabled,
      notificationEmail: formValues.notificationEnabled ? formValues.notificationEmail.trim() : undefined,
      canBookSameDay: formValues.canBookSameDay,
      maxDaysInAdvance: parseAsNumber(formValues.maxDaysInAdvance),
      hoursForMutations: parseAsNumber(formValues.hoursForMutations),
      requireBookingReason: formValues.requireBookingReason,
      publishAt:
        formValues.publishAt && formValues.publishAt > new Date() ? formValues.publishAt.toISOString() : undefined,
      availableFrom: formValues.availableFrom ? formValues.availableFrom.toISOString() : undefined,
      unpublishAt: formValues.unpublishAt ? formValues.unpublishAt.toISOString() : undefined,
      seats: formValues.seats || defaultAmountSeats,
      enabled: true,
      externalBookingProvider: formValues.externalBookingProvider,
      externalResourceId: formValues.externalResourceId?.trim() || undefined,
    };

    if (formValues.audience && !sessionUser.isAdmin) {
      data.audience = [];
    }

    createAssetMutation.mutate(
      {
        data: {
          ...data,
          projectConnectionId: formValues.assetObserver.type === "project" ? undefined : formValues.assetObserver.id,
        },
      },
      {
        onSuccess: () => {
          void navigate(routes.bookings.list({ slug }));
        },
      },
    );
  }

  const isLoading = isFetchingOutlookConfiguration || isFetchingJimConfiguration || isFetchingJimRooms;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  // A list of projects or project connections that book this asset
  const assetObservers: AssetObserver[] = [
    { type: "project", id: sessionUser.project.id, name: sessionUser.project.name },
    ...sessionUser.connections.map((x) => ({
      type: "projectConnection" as const,
      id: x.id,
      name: x.name,
    })),
  ];

  const formProps: BookableAssetFormProps = {
    asset: undefined,
    assetObservers,
    getBookingsToBeCancelled,
    onSubmit: handleSubmit,
    isSubmitting:
      createAssetMutation.isPending || isUploadingImage || isUploadingDocument || isPendingBookingsToBeCancelled,
    isEditMode: false,
    canEditTimeslot: true,
    futureBookings: 0,
    defaultValues: getDefaultFormValues(assetObservers, sessionUser.email),
    isPublished: false,
    enabledExternalProviders: [
      ...(outlookConfiguration?.enabled ? (["outlook"] as const) : []),
      ...(jimConfiguration?.enabled ? (["jim"] as const) : []),
    ],
    jimRooms,
  };

  return <BookableAssetForm {...formProps} />;
}
